import React, { useEffect, useState } from "react";
import Select from "react-select";
import FileUpload from "./FileUpload";
import VideoUpload from "./VideoUpload";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import {
  GetCategoriesType,
  attachMediaAndCompleteInstallationJob,
} from "../../../models/_publicCall";
import SimpleMobileStepper from "../../Stepper/Stepper";
import { InstallationJobStepsList } from "../InstallationSteps";

interface PictureAndVideoProps {
  jobId: string;
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setCurrentStep: (step: number) => void;
  InstalltionFormSteps: any;
  categoriesDB: GetCategoriesType[];
  jobStatus: string;
  installationJobDB: any;
  isFormSubmitted: boolean;
  setIsFormSubmitted: (submitted: boolean) => void;
  installationJobStepsRequired: { id: number; label: string }[];
  fetchInstallationJob: (id: string) => Promise<any>;
  setToast: (message: string, type: "success" | "error") => void;
  setShowJobCompleted: (show: boolean) => void;
}

type RequiredPhotoKey =
  | "fullSetup"
  | "freezerOpen"
  | "backOfMachine"
  | "relativeToEntrance";

const PictureAndVideo: React.FC<PictureAndVideoProps> = ({
  jobId,
  values,
  setFieldValue,
  setCurrentStep,
  InstalltionFormSteps,
  categoriesDB,
  jobStatus,
  installationJobDB,
  isFormSubmitted,
  setIsFormSubmitted,
  installationJobStepsRequired,
  fetchInstallationJob,
  setToast,
  setShowJobCompleted,
}) => {
  const [notes, setNotes] = useState<
    { categoryId: string | null; note: string }[]
  >([]);

  const [errors, setErrors] = useState<{
    [key in RequiredPhotoKey | "video"]?: string;
  }>({});

  const initialPhotos: {
    key: RequiredPhotoKey;
    label: string;
    showMandatory?: boolean;
  }[] = [
    {
      key: "fullSetup",
      label: "Photo of full smoodi setup",
      showMandatory: true,
    },
    { key: "freezerOpen", label: "Photo of setup with freezer door open" },
    { key: "backOfMachine", label: "Photo of the back of the machine" },
    {
      key: "relativeToEntrance",
      label: "Photo showing machine relative to the store entrance",
    },
  ];

  const handleBack = () =>
    setCurrentStep(InstalltionFormSteps.LOCATION_CONTACTS);

  // 🔹 Prefill Formik fields + localStorage on mount
  useEffect(() => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );
    const locationNotes = storedData.locationNotes || {};

    // Start with localStorage
    const updatedNotes: any = { ...locationNotes };

    // Prefill fields from localStorage
    initialPhotos.forEach((photo) => {
      const value = locationNotes[photo.key] || [];
      setFieldValue(photo.key, value);
      updatedNotes[photo.key] = value;
    });
    const video = locationNotes.video || [];
    setFieldValue("video", video);
    updatedNotes.video = video;

    let loadedNotes: { categoryId: string | null; note: string }[] = [];
    if (locationNotes.notes && Array.isArray(locationNotes.notes)) {
      loadedNotes = locationNotes.notes;
    } else {
      // Migrate from old fields
      if (locationNotes.additionalComments) {
        loadedNotes.push({
          categoryId: locationNotes.additionalNoteCategoryId || null,
          note: locationNotes.additionalComments,
        });
      }
      if (locationNotes.actionItems) {
        loadedNotes.push({
          categoryId: locationNotes.actionNoteCategoryId || null,
          note: locationNotes.actionItems,
        });
      }
    }
    // Ensure at least one note box
    if (loadedNotes.length === 0) {
      loadedNotes = [{ categoryId: null, note: "" }];
    }
    setNotes(loadedNotes);
    setFieldValue("locationNotes", loadedNotes);
    updatedNotes.notes = loadedNotes;

    // If job not completed and DB exists, prefill from DB if localStorage empty
    const isLocalEmpty =
      initialPhotos.every((p) => updatedNotes[p.key]?.length === 0) &&
      updatedNotes.video.length === 0 &&
      loadedNotes.length === 0;

    if (jobStatus !== "Completed" && installationJobDB && isLocalEmpty) {
      const dbUploads = installationJobDB.uploadImagesOrVideos || {};
      const dbNotes = {
        fullSetup: dbUploads.imageOfFullSmoodiSetUp || [],
        freezerOpen: dbUploads.setupFreezerDoorPhoto || [],
        backOfMachine: dbUploads.machineBackPhoto || [],
        relativeToEntrance: dbUploads.machineEntranceViewPhoto || [],
        video: dbUploads.storeWalkthroughVideo || [],
      };

      // Set Formik
      Object.entries(dbNotes).forEach(([key, value]) =>
        setFieldValue(key, value),
      );

      // Load notes from DB
      const dbLoadedNotes: { categoryId: string | null; note: string }[] = [];
      if (installationJobDB.additionalComments) {
        dbLoadedNotes.push({
          categoryId: installationJobDB.additionalNoteCategoryId || null,
          note: installationJobDB.additionalComments,
        });
      }
      if (installationJobDB.actionItems) {
        dbLoadedNotes.push({
          categoryId: installationJobDB.actionNoteCategoryId || null,
          note: installationJobDB.actionItems,
        });
      }
      // Ensure at least one note box
      if (dbLoadedNotes.length === 0) {
        dbLoadedNotes.push({ categoryId: null, note: "" });
      }
      setNotes(dbLoadedNotes);
      setFieldValue("locationNotes", dbLoadedNotes);
      updatedNotes.notes = dbLoadedNotes;

      // Update localStorage
      Object.assign(updatedNotes, dbNotes);
    }

    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify({
        ...storedData,
        locationNotes: updatedNotes,
      }),
    );
  }, [installationJobDB, jobStatus, setFieldValue]);

  const updateLocalStorage = (updates: Record<string, any>) => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );
    const updatedData = {
      ...storedData,
      locationNotes: {
        ...storedData.locationNotes,
        ...updates,
      },
    };
    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify(updatedData),
    );
  };

  const updateNotes = (
    newNotes: { categoryId: string | null; note: string }[],
  ) => {
    setNotes(newNotes);
    setFieldValue("locationNotes", newNotes);
    updateLocalStorage({ notes: newNotes });
  };

  const addNote = () => {
    const newNotes = [...notes, { categoryId: null, note: "" }];
    updateNotes(newNotes);
  };

  const removeNote = (index: number) => {
    const newNotes = notes.filter((_, i) => i !== index);
    updateNotes(newNotes);
  };

  const updateNoteCategory = (index: number, categoryId: string | null) => {
    const newNotes = notes.map((note, i) =>
      i === index ? { ...note, categoryId } : note,
    );
    updateNotes(newNotes);
  };

  const updateNoteText = (index: number, note: string) => {
    const newNotes = notes.map((n, i) => (i === index ? { ...n, note } : n));
    updateNotes(newNotes);
  };

  const categoryOptions = categoriesDB.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const canAddMore = notes.every(
    (note) => note.categoryId !== null && note.note.trim() !== "",
  );

  const validateBeforeSubmit = (): boolean => {
    setErrors({});
    // Use current Formik values instead of localStorage to avoid timing issues
    const newErrors: typeof errors = {};

    initialPhotos.forEach((photo) => {
      if (photo.key !== "fullSetup") return; // Only require fullSetup photo

      const images = values[photo.key];
      if (!images || !Array.isArray(images) || images.length === 0) {
        newErrors[photo.key] = `Please upload ${photo.label}`;
      }
    });

    // const video = values.video;
    // if (!video || !Array.isArray(video) || video.length === 0) {
    //   newErrors.video = "Please upload walkthrough video";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) {
      return;
    }
    setIsFormSubmitted(true);

    const freshStoredData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );

    const $inputData: any = {
      // --- Upload Images/Videos ---
      uploadImagesOrVideos: {
        imageOfFullSmoodiSetUp: freshStoredData?.locationNotes?.fullSetup || [],
        setupFreezerDoorPhoto:
          freshStoredData?.locationNotes?.freezerOpen || [],
        machineBackPhoto: freshStoredData?.locationNotes?.backOfMachine || [],
        machineEntranceViewPhoto:
          freshStoredData?.locationNotes?.relativeToEntrance || [],
        storeWalkthroughVideo: freshStoredData?.locationNotes?.video || [],
      },

      // --- Additional Notes ---
      notes: (freshStoredData?.locationNotes?.notes || []).filter(
        (a: any) => a.categoryId,
      ),
    };

    const response = await attachMediaAndCompleteInstallationJob(
      jobId,
      $inputData,
    );

    if (response?.isSuccess) {
      setToast("Job updated successfully", "success");
      setShowJobCompleted(true);
      //localStorage.removeItem(INSTALLATION_JOB_STORAGE_KEY);
      fetchInstallationJob(jobId);
      localStorage.removeItem(INSTALLATION_JOB_STORAGE_KEY);
      setCurrentStep(InstalltionFormSteps.SUBMIT); // move to preview step
    } else {
      setToast(response.message, "error");
    }
    setIsFormSubmitted(false);
    // const form = document.querySelector("form");
    // if (form) form.requestSubmit();
  };

  return (
    <div>
      <header className="header">
        <h2>Location Notes</h2>
      </header>

      {/* <div className="mb-4">
        <div className="left-icon mb-4 mt-4 ms-2" onClick={handleBack} style={{ cursor: "pointer" }}>
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </div>
      </div> */}

      <div className="job-wrapper">
        {/* Required Photos */}
        {initialPhotos.map((photo) => (
          <div key={photo.key} className="mb-3">
            <FileUpload
              jobId={jobId}
              values={values}
              setFieldValue={setFieldValue}
              fieldName={photo.key}
              label={photo.label}
              installationJobDB={installationJobDB}
              jobStatus={jobStatus}
              showMandatory={photo.showMandatory}
            />
            {errors[photo.key] && (
              <div className="text-danger mt-1">{errors[photo.key]}</div>
            )}
          </div>
        ))}

        {/* Video Upload */}
        <div className="mb-3">
          <VideoUpload
            values={values}
            setFieldValue={setFieldValue}
            fieldName="video"
            label="Walkthrough entering the store and approaching the machine"
            installationJobDB={installationJobDB}
            jobStatus={jobStatus}
          />
          {errors.video && (
            <div className="text-danger mt-1">{errors.video}</div>
          )}
        </div>

        {/* Dynamic Location Notes */}
        <div className="mt-4 mb-3">
          <label className="wo-label fs-4 mb-3 fw-bold">Location Notes</label>
          {notes.map((note, index) => (
            <div key={index} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Note {index + 1}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeNote(index)}
                >
                  Remove
                </button>
              </div>
              <Select
                className="react-select mb-2"
                classNamePrefix="my-react-select"
                value={
                  note.categoryId
                    ? categoryOptions.find(
                        (opt) => opt.value === note.categoryId,
                      )
                    : null
                }
                options={categoryOptions}
                placeholder="Select Category"
                onChange={(option) =>
                  updateNoteCategory(index, option ? option.value : null)
                }
              />
              <textarea
                className="form-control"
                value={note.note}
                onChange={(e) => updateNoteText(index, e.target.value)}
                placeholder="Enter note"
                rows={3}
              />
            </div>
          ))}
          <button
            type="button"
            className="btn btn-primary"
            onClick={addNote}
            disabled={!canAddMore}
          >
            Add More
          </button>
        </div>
      </div>
      <div className="col-12 mt-4 text-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isFormSubmitted}
        >
          <i className="fa fa-long-arrow-right me-1" aria-hidden="true"></i>{" "}
          {isFormSubmitted ? "Submitting..." : "Submit"}
        </button>
      </div>
      {/* <SimpleMobileStepper
        totalSteps={installationJobStepsRequired.length}
        handleNext={handleSubmit}
        handleBack={() =>
          setCurrentStep(InstalltionFormSteps.LOCATION_CONTACTS)
        }
        activeStep={installationJobStepsRequired.findIndex(
          (s) => s.id === InstalltionFormSteps.PICTURES_AND_VIDEO,
        )}
        isFormSubmitted={isFormSubmitted}
        isLastStep={true}
      /> */}
    </div>
  );
};

export default PictureAndVideo;
